import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import partyService from '../../services/partyService';
import masterService from '../../services/masterService';
import profileService from '../../services/profileService';
import packageService from '../../services/packageService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import MasterToolbar from './components/MasterToolbar';
import MasterTable from './components/MasterTable';
import StatusBadge from './components/StatusBadge';
import ActionMenu from './components/ActionMenu';
import ConfirmModal from './components/ConfirmModal';
import usePagination from '../../hooks/usePagination';
import './PackageMasterPage.css';

const INITIAL_CONTRACT = {
  contractId: 0,
  contractNumber: '',
  contractName: '',
  partyId: '',
  validFrom: new Date().toISOString().split('T')[0],
  validTo: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
  remarks: '',
  isActive: true
};

export const RateContractPage = ({ showToast = () => {} }) => {
  const [searchParams] = useSearchParams();
  const partyParam = searchParams.get('partyId');

  const [viewMode, setViewMode] = useState('list');
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form State
  const [formData, setFormData] = useState(INITIAL_CONTRACT);
  const [errors, setErrors] = useState({});

  // Lookups
  const [parties, setParties] = useState([]);

  // List State
  const [contracts, setContracts] = useState([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [partyFilter, setPartyFilter] = useState(partyParam || '');
  const [statusFilter, setStatusFilter] = useState('all');

  // Centralized Pagination
  const { paginatedItems: pagedContracts, paginationProps: contractPaginationProps } = usePagination({
    items: contracts,
    resetDeps: [searchQuery, partyFilter, statusFilter]
  });

  // Items Drawer / Management State
  const [activeContractForItems, setActiveContractForItems] = useState(null);
  const [contractItems, setContractItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Add Item Row State
  const [newItemType, setNewItemType] = useState('TEST'); // 'TEST' | 'PROFILE' | 'PACKAGE'
  const [newItemId, setNewItemId] = useState('');
  const [newRate, setNewRate] = useState('');
  const [newMrp, setNewMrp] = useState('');
  const [newRemarks, setNewRemarks] = useState('');
  const [addingItem, setAddingItem] = useState(false);

  // Catalogs for Item Selection
  const [allTests, setAllTests] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [allPackages, setAllPackages] = useState([]);

  // Confirm Modal
  const [confirmTarget, setConfirmTarget] = useState(null);

  // Load Parties
  const loadParties = useCallback(async () => {
    try {
      const res = await partyService.getParties({ isActive: true });
      if (res && res.success) {
        setParties(res.data || []);
      }
    } catch (err) {
      console.error('Error loading parties:', err);
    }
  }, []);

  // Load Catalogs
  const loadCatalogs = useCallback(async () => {
    try {
      const [testsRes, profRes, pkgRes] = await Promise.all([
        masterService.getInvestigations({ isActive: true, pageSize: 1000 }),
        profileService.getProfiles({ isActive: true }),
        packageService.getPackages({ isActive: true })
      ]);

      if (testsRes && testsRes.success) setAllTests(testsRes.data?.items || testsRes.data || []);
      if (profRes && profRes.success) setAllProfiles(profRes.data || []);
      if (pkgRes && pkgRes.success) setAllPackages(pkgRes.data || []);
    } catch (err) {
      console.error('Error loading item catalogs:', err);
    }
  }, []);

  // Load Contracts
  const loadContracts = useCallback(async () => {
    setLoadingContracts(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (partyFilter) params.partyId = parseInt(partyFilter, 10);

      const res = await partyService.getContracts(params);
      if (res && res.success) {
        setContracts(res.data || []);
      }
    } catch (err) {
      console.error('Error loading contracts:', err);
      showToast(err.response?.data?.message || 'Failed to load contracts', 'error');
    } finally {
      setLoadingContracts(false);
    }
  }, [searchQuery, partyFilter, statusFilter, showToast]);

  useEffect(() => {
    loadParties();
    loadCatalogs();
  }, [loadParties, loadCatalogs]);

  useEffect(() => {
    if (viewMode === 'list') {
      loadContracts();
    }
  }, [viewMode, loadContracts]);

  // Open Create Form
  const handleAddNew = () => {
    setFormData({
      ...INITIAL_CONTRACT,
      partyId: partyFilter || ''
    });
    setErrors({});
    setIsEditMode(false);
    setViewMode('form');
  };

  // Open Edit Form
  const handleEdit = (c) => {
    setIsEditMode(true);
    setFormData({
      contractId: c.contractId,
      contractNumber: c.contractNumber || '',
      contractName: c.contractName || '',
      partyId: c.partyId || '',
      validFrom: c.validFrom ? c.validFrom.split('T')[0] : '',
      validTo: c.validTo ? c.validTo.split('T')[0] : '',
      remarks: c.remarks || '',
      isActive: c.isActive
    });
    setErrors({});
    setViewMode('form');
  };

  // Status Toggle
  const handleToggleStatus = (c) => {
    setConfirmTarget({
      contractId: c.contractId,
      contractName: c.contractName,
      nextStatus: !c.isActive
    });
  };

  const confirmStatusChange = async () => {
    if (!confirmTarget) return;
    try {
      await partyService.updateContractStatus(confirmTarget.contractId, confirmTarget.nextStatus);
      showToast(`Contract ${confirmTarget.nextStatus ? 'activated' : 'deactivated'} successfully.`, 'success');
      setConfirmTarget(null);
      loadContracts();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update contract status', 'error');
    }
  };

  // Load Contract Items
  const handleOpenItems = async (c) => {
    setActiveContractForItems(c);
    setLoadingItems(true);
    try {
      const res = await partyService.getContractItems(c.contractId);
      if (res && res.success) {
        setContractItems(res.data || []);
      }
    } catch (err) {
      console.error('Error loading contract items:', err);
      showToast('Could not load contract tariff items', 'error');
    } finally {
      setLoadingItems(false);
    }
  };

  // Add Item to Contract
  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItemId || !newRate) {
      showToast('Please select an item and enter contract rate', 'warning');
      return;
    }

    setAddingItem(true);
    try {
      await partyService.addContractItem(activeContractForItems.contractId, {
        itemType: newItemType,
        itemId: parseInt(newItemId, 10),
        contractRate: parseFloat(newRate),
        mrpRate: newMrp ? parseFloat(newMrp) : parseFloat(newRate),
        specialRemarks: newRemarks.trim() || null
      });

      showToast('Contract item rate saved.', 'success');
      setNewItemId('');
      setNewRate('');
      setNewMrp('');
      setNewRemarks('');

      // Refresh items
      const res = await partyService.getContractItems(activeContractForItems.contractId);
      if (res && res.success) setContractItems(res.data || []);
      loadContracts();
    } catch (err) {
      console.error('Error adding contract item:', err);
      showToast(err.response?.data?.message || 'Failed to save item rate', 'error');
    } finally {
      setAddingItem(false);
    }
  };

  // Remove Item
  const handleRemoveItem = async (itemId) => {
    try {
      await partyService.removeContractItem(itemId);
      showToast('Contract item removed.', 'success');
      setContractItems(prev => prev.filter(i => i.id !== itemId));
      loadContracts();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove item', 'error');
    }
  };

  // Items filtered for select box based on newItemType
  const selectableCatalog = useMemo(() => {
    if (newItemType === 'TEST') {
      return allTests.map(t => ({ id: t.investigationId, name: `${t.name} (${t.testCode})`, rate: t.baseRate }));
    } else if (newItemType === 'PROFILE') {
      return allProfiles.map(p => ({ id: p.profileId, name: `${p.profileName} (${p.profileCode})`, rate: 0 }));
    } else {
      return allPackages.map(pkg => ({ id: pkg.packageId, name: `${pkg.packageName} (${pkg.packageCode})`, rate: 0 }));
    }
  }, [newItemType, allTests, allProfiles, allPackages]);

  // Validation
  const validateForm = () => {
    const errs = {};
    if (!formData.contractNumber.trim()) errs.contractNumber = 'Contract number is required';
    if (!formData.contractName.trim()) errs.contractName = 'Contract name is required';
    if (!formData.partyId) errs.partyId = 'Please select a Party / Client';
    if (!formData.validFrom || !formData.validTo) errs.dates = 'Validity dates are required';
    if (formData.validFrom > formData.validTo) errs.dates = 'ValidFrom cannot be after ValidTo';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Save Contract
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast('Please fix required fields.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        contractNumber: formData.contractNumber.trim().toUpperCase(),
        contractName: formData.packageName || formData.contractName.trim(),
        partyId: parseInt(formData.partyId, 10),
        validFrom: formData.validFrom,
        validTo: formData.validTo,
        remarks: formData.remarks?.trim() || null
      };

      if (isEditMode) {
        await partyService.updateContract(formData.contractId, {
          ...payload,
          isActive: formData.isActive
        });
        showToast('Contract updated successfully.', 'success');
      } else {
        const res = await partyService.createContract(payload);
        showToast('Rate Contract created successfully.', 'success');
        if (res && res.data) {
          // Open item management directly
          handleOpenItems(res.data);
        }
      }

      setViewMode('list');
      loadContracts();
    } catch (err) {
      console.error('Error saving contract:', err);
      showToast(err.response?.data?.message || 'Error saving contract', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Table Columns
  const columns = [
    {
      key: 'contractNumber',
      header: 'Contract No',
      render: (row) => <strong style={{ color: 'var(--primary-600)' }}>{row.contractNumber}</strong>
    },
    {
      key: 'contractName',
      header: 'Contract Description',
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.contractName}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            Client: <strong>{row.partyName}</strong> ({row.classificationName})
          </div>
        </div>
      )
    },
    {
      key: 'validity',
      header: 'Validity Period',
      render: (row) => (
        <span style={{ fontSize: '0.82rem' }}>
          {row.validFrom?.split('T')[0]} to {row.validTo?.split('T')[0]}
        </span>
      )
    },
    {
      key: 'itemCount',
      header: 'Contracted Rates',
      render: (row) => (
        <span style={{
          background: row.itemCount > 0 ? '#e0e7ff' : '#f1f5f9',
          color: row.itemCount > 0 ? '#3730a3' : '#64748b',
          padding: '0.15rem 0.55rem',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: 600
        }}>
          {row.itemCount || 0} Items
        </span>
      )
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (row) => <StatusBadge isActive={row.isActive} />
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <ActionMenu
          actions={[
            {
              label: 'Configure Tariff Rates',
              onClick: () => handleOpenItems(row)
            },
            {
              label: 'Edit Contract Details',
              onClick: () => handleEdit(row)
            },
            {
              label: row.isActive ? 'Deactivate' : 'Activate',
              onClick: () => handleToggleStatus(row),
              danger: row.isActive
            }
          ]}
        />
      )
    }
  ];

  return (
    <MasterPageWrapper
      masterTitle="B2B Rate Contract Master"
      masterName="Rate Contract"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form') handleAddNew();
        else setViewMode('list');
      }}
    >
      {viewMode === 'list' ? (
        <>
          <MasterToolbar
            searchPlaceholder="Search Contract Number, Name, Client..."
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            filters={[
              {
                id: 'partyFilter',
                label: 'Client / Party',
                value: partyFilter,
                onChange: setPartyFilter,
                options: [
                  { value: '', label: 'All B2B Parties' },
                  ...parties.map(p => ({ value: String(p.partyId), label: `${p.partyName} (${p.partyCode})` }))
                ]
              },
              {
                id: 'statusFilter',
                label: 'Status',
                value: statusFilter,
                onChange: setStatusFilter,
                options: [
                  { value: 'all', label: 'All Statuses' },
                  { value: 'active', label: 'Active Only' },
                  { value: 'inactive', label: 'Inactive Only' }
                ]
              }
            ]}
          />

          <MasterTable
            columns={columns}
            data={pagedContracts}
            loading={loadingContracts}
            emptyTitle="No Rate Contracts Configured"
            emptyDescription="Create contractual price lists for corporate tie-ups and referral hospitals."
            pagination={contractPaginationProps}
          />
        </>
      ) : (
        <form onSubmit={handleSave} className="fade-in">
          <FormSection
            title="Contract Terms & Identification"
            description="Specify contract identifier, associated B2B client party, and validity date range."
          >
            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">Contract Number / Code</label>
                <input
                  type="text"
                  className={`form-input ${errors.contractNumber ? 'is-invalid' : ''}`}
                  placeholder="e.g. CTR-CORP-2026, HOSP-APOLLO-01"
                  value={formData.contractNumber}
                  onChange={(e) => setFormData({ ...formData, contractNumber: e.target.value.toUpperCase() })}
                  disabled={isEditMode}
                />
                {errors.contractNumber && <span className="form-error">{errors.contractNumber}</span>}
              </div>

              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label required">Contract Title / Description</label>
                <input
                  type="text"
                  className={`form-input ${errors.contractName ? 'is-invalid' : ''}`}
                  placeholder="e.g. Corporate Employee Screening Tariff 2026"
                  value={formData.contractName}
                  onChange={(e) => setFormData({ ...formData, contractName: e.target.value })}
                />
                {errors.contractName && <span className="form-error">{errors.contractName}</span>}
              </div>

              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label required">B2B Client Party</label>
                <select
                  className={`form-input ${errors.partyId ? 'is-invalid' : ''}`}
                  value={formData.partyId}
                  onChange={(e) => setFormData({ ...formData, partyId: e.target.value })}
                  disabled={isEditMode}
                >
                  <option value="">-- Select B2B Party --</option>
                  {parties.map(p => (
                    <option key={p.partyId} value={p.partyId}>
                      {p.partyName} ({p.partyCode})
                    </option>
                  ))}
                </select>
                {errors.partyId && <span className="form-error">{errors.partyId}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">Valid From</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.validFrom}
                  onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label className="form-label required">Valid To</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.validTo}
                  onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
                />
              </div>

              <div className="form-group" style={{ flex: 2 }}>
                <label className="form-label">Internal Remarks</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Special executive health checkup discount terms"
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                />
              </div>
            </div>
            {errors.dates && <div className="form-error" style={{ marginBottom: '1rem' }}>{errors.dates}</div>}
          </FormSection>

          <div className="form-actions" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setViewMode('list')}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? 'Saving...' : isEditMode ? 'Update Contract' : 'Create Contract'}
            </button>
          </div>
        </form>
      )}

      {/* Contract Items Modal / Tariff Sheet */}
      {activeContractForItems && (
        <div className="pkg-resolved-modal-backdrop" onClick={() => setActiveContractForItems(null)}>
          <div className="pkg-resolved-modal" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
            <div className="pkg-resolved-header">
              <div>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                  Contract Tariff — {activeContractForItems.contractNumber} ({activeContractForItems.partyName})
                </h2>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Valid: {activeContractForItems.validFrom?.split('T')[0]} to {activeContractForItems.validTo?.split('T')[0]}
                </span>
              </div>
              <button
                type="button"
                className="pf-btn-icon"
                onClick={() => setActiveContractForItems(null)}
              >
                ✕
              </button>
            </div>

            <div className="pkg-resolved-body">
              {/* Quick-Add Item Form */}
              <form onSubmit={handleAddItem} style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '0.85rem',
                marginBottom: '1.25rem'
              }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155', marginBottom: '0.5rem' }}>
                  + Add / Override Contract Rate
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 2fr 110px 110px auto', gap: '0.5rem', alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#64748b', display: 'block', marginBottom: '0.2rem' }}>Type</label>
                    <select
                      className="form-input"
                      style={{ fontSize: '0.82rem', padding: '0.35rem 0.5rem' }}
                      value={newItemType}
                      onChange={(e) => {
                        setNewItemType(e.target.value);
                        setNewItemId('');
                      }}
                    >
                      <option value="TEST">Investigation</option>
                      <option value="PROFILE">Profile</option>
                      <option value="PACKAGE">Package</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#64748b', display: 'block', marginBottom: '0.2rem' }}>Select Item</label>
                    <select
                      className="form-input"
                      style={{ fontSize: '0.82rem', padding: '0.35rem 0.5rem' }}
                      value={newItemId}
                      onChange={(e) => setNewItemId(e.target.value)}
                    >
                      <option value="">-- Choose {newItemType.toLowerCase()} --</option>
                      {selectableCatalog.map(item => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#64748b', display: 'block', marginBottom: '0.2rem' }}>Contract (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      style={{ fontSize: '0.82rem', padding: '0.35rem 0.5rem' }}
                      placeholder="e.g. 250"
                      value={newRate}
                      onChange={(e) => setNewRate(e.target.value)}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#64748b', display: 'block', marginBottom: '0.2rem' }}>MRP (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-input"
                      style={{ fontSize: '0.82rem', padding: '0.35rem 0.5rem' }}
                      placeholder="e.g. 400"
                      value={newMrp}
                      onChange={(e) => setNewMrp(e.target.value)}
                    />
                  </div>

                  <div>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{ fontSize: '0.82rem', padding: '0.4rem 0.85rem' }}
                      disabled={addingItem}
                    >
                      {addingItem ? 'Adding...' : 'Add Rate'}
                    </button>
                  </div>
                </div>
              </form>

              {/* Items Table */}
              {loadingItems ? (
                <div className="pf-empty-placeholder">Loading contracted rates...</div>
              ) : contractItems.length === 0 ? (
                <div className="pf-empty-placeholder">
                  No contracted rates added yet.<br />
                  Add special B2B tariffs for this contract above.
                </div>
              ) : (
                <table className="master-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem' }}>Type</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem' }}>Code</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem' }}>Item Name</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem' }}>Department</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem', textAlign: 'right' }}>Contract Rate</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem', textAlign: 'right' }}>MRP</th>
                      <th style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contractItems.map((item) => (
                      <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '0.65rem 0.75rem' }}>
                          <span className={`pkg-type-badge ${item.itemType === 'TEST' ? 'pkg-type-test' : 'pkg-type-profile'}`}>
                            {item.itemType}
                          </span>
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.85rem' }}>
                          <code>{item.itemCode || '—'}</code>
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.875rem', fontWeight: 600 }}>
                          {item.itemName}
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.85rem', color: '#64748b' }}>
                          {item.departmentName || '—'}
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.9rem', fontWeight: 700, color: '#047857', textAlign: 'right' }}>
                          ₹{parseFloat(item.contractRate).toFixed(2)}
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.85rem', color: '#94a3b8', textAlign: 'right' }}>
                          ₹{parseFloat(item.mrpRate).toFixed(2)}
                        </td>
                        <td style={{ padding: '0.65rem 0.75rem', textAlign: 'center' }}>
                          <button
                            type="button"
                            className="pf-btn-icon pf-btn-remove"
                            onClick={() => handleRemoveItem(item.id)}
                            title="Remove Rate"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="pkg-resolved-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setActiveContractForItems(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Status Modal */}
      {confirmTarget && (
        <ConfirmModal
          isOpen={true}
          title={`${confirmTarget.nextStatus ? 'Activate' : 'Deactivate'} Contract`}
          message={`Are you sure you want to ${confirmTarget.nextStatus ? 'activate' : 'deactivate'} contract "${confirmTarget.contractName}"?`}
          confirmLabel={confirmTarget.nextStatus ? 'Activate' : 'Deactivate'}
          confirmVariant={confirmTarget.nextStatus ? 'primary' : 'danger'}
          onConfirm={confirmStatusChange}
          onCancel={() => setConfirmTarget(null)}
        />
      )}
    </MasterPageWrapper>
  );
};

export default RateContractPage;
